import { zodResolver } from '@hookform/resolvers/zod';
import {
    Form, Input, InputNumber, Spin, Tooltip,
} from 'antd';
import {
    forwardRef, useEffect, useImperativeHandle, useMemo, useRef,
} from 'react';
import {
    Controller, useForm,
} from 'react-hook-form';
import { type To } from 'react-router-dom';
import { z } from 'zod';

import { Objection } from '@/types';

import {
    FormPrimaryActionsRow, FormSaveFloatButton,
} from './FormSubmitChrome';
import { TextEditor } from './TextEditor';

const OBJECTION_FORM_DOM_ID = 'objection-form';

const objectionSchema = z.object({
    title: z.string().min(1, 'Назва обов\'язкова').max(255, 'Максимум 255 символів').trim(),
    body: z.union([z.string().max(10000, 'Максимум 10000 символів'), z.null()]).optional(),
    priority: z.number().int().min(1, 'Мінімальне значення 1').max(50000, 'Максимальне значення 50000').optional(),
});

type ObjectionFormData = z.infer<typeof objectionSchema>;

type ObjectionFormProps = {
    initData?: Objection | null;
    onSubmit: (data: ObjectionFormData) => void;
    onCancel?: () => void;
    loading?: boolean;
    cancelButtonText?: string;
    cancelTo?: To;
};

export type ObjectionFormRef = {
    reset: () => void;
};

export const ObjectionForm = forwardRef<ObjectionFormRef, ObjectionFormProps>(({
    initData,
    onSubmit,
    onCancel,
    loading = false,
    cancelButtonText,
    cancelTo,
}, ref) => {
    const hiddenSubmitRef = useRef<HTMLButtonElement>(null);

    const defaultValues = useMemo<ObjectionFormData>(() => ({
        title: initData?.title || '',
        body: initData?.body || null,
        priority: initData?.priority ?? 10,
    }), [initData]);

    const {
        control,
        handleSubmit,
        reset,
        formState: { errors, isDirty },
    } = useForm<ObjectionFormData>({
        resolver: zodResolver(objectionSchema),
        defaultValues,
        mode: 'onChange',
    });

    useEffect(() => {
        if (initData) {
            reset(defaultValues, { keepDirty: true });
        }
    }, [initData?.id, defaultValues, reset, initData]);

    useImperativeHandle(ref, () => ({
        reset: () => {
            reset(defaultValues);
        },
    }), [defaultValues, reset]);

    const primaryDisabled = loading || (!!initData && !isDirty);
    const submitLabel = initData ? 'Оновити' : 'Створити';

    return (
        <Spin spinning={loading}>
            <Form
                id={OBJECTION_FORM_DOM_ID}
                layout="vertical"
                onFinish={handleSubmit(onSubmit)}
            >
                <FormPrimaryActionsRow
                    onCancel={onCancel}
                    cancelButtonText={cancelButtonText}
                    cancelTo={cancelTo}
                    loading={loading}
                    submitDisabled={primaryDisabled}
                    submitLabel={submitLabel}
                    style={{ marginBottom: 24 }}
                />
                <Controller
                    name="title"
                    control={control}
                    render={({ field }) => (
                        <Form.Item
                            label="Назва"
                            required
                            validateStatus={errors.title ? 'error' : ''}
                            help={errors.title?.message}
                        >
                            <Input
                                {...field}
                                placeholder="Введіть назву заперечення"
                                maxLength={255}
                                showCount
                                disabled={loading}
                            />
                        </Form.Item>
                    )}
                />

                <Controller
                    name="priority"
                    control={control}
                    render={({ field }) => (
                        <Form.Item
                            label={
                                <Tooltip title="Менше число - вища приорітетність. Більше число - менша приорітетність">
                                    <span>Пріоритет</span>
                                </Tooltip>
                            }
                            validateStatus={errors.priority ? 'error' : ''}
                            help={errors.priority?.message}
                        >
                            <InputNumber
                                {...field}
                                value={field.value ?? 10}
                                onChange={(value) => field.onChange(value ?? 10)}
                                placeholder="Введіть пріоритет"
                                min={1}
                                max={50000}
                                style={{ width: '100%' }}
                                disabled={loading}
                            />
                        </Form.Item>
                    )}
                />

                <Controller
                    name="body"
                    control={control}
                    render={({ field }) => (
                        <Form.Item
                            label="Опис"
                            validateStatus={errors.body ? 'error' : ''}
                            help={errors.body?.message}
                        >
                            {/*<TextEditorWarning />*/}
                            <TextEditor
                                value={field.value || ''}
                                onChange={field.onChange}
                                disabled={loading}
                            />
                        </Form.Item>
                    )}
                />

                <FormPrimaryActionsRow
                    onCancel={onCancel}
                    cancelButtonText={cancelButtonText}
                    cancelTo={cancelTo}
                    loading={loading}
                    submitDisabled={primaryDisabled}
                    submitLabel={submitLabel}
                    style={{ marginTop: 24 }}
                />
            </Form>
            <FormSaveFloatButton
                formDomId={OBJECTION_FORM_DOM_ID}
                hiddenSubmitRef={hiddenSubmitRef}
                loading={loading}
                submitDisabled={primaryDisabled}
                submitLabel={submitLabel}
            />
        </Spin>
    );
});
